import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult
} from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import {
  requestStepUpGrant,
  STEP_UP_FAILURE_TEXT,
  type StepUpAction,
  type StepUpFailure,
  type StepUpResult
} from "../auth/stepUp";
import { toPortalFailure } from "../lib/errors";

/**
 * The browser half of plan.md section 5.10, which had none.
 *
 * Everything under this module already existed and none of it was reachable from a screen. The
 * Worker encrypts, decrypts, rate limits and audits at `/api/secrets/*`; the database owns tenancy,
 * the recipient's verified MFA, the step-up grant, the single conditional view and the refusal
 * audit. `tests/worker-route-reachability.test.ts` listed `/api/secrets/` and `/api/secrets/create`
 * as two of its three known defects for exactly that reason: a complete feature with no caller.
 *
 * ---
 *
 * **The rule this file exists to keep, and every decision below follows from it.** plan.md 5.10:
 * "Email, push, analytics, logs and audit events never contain the value", and 14.4 makes one
 * conditional statement the only way a ciphertext leaves the database. In a browser the equivalent
 * risk is not a log line, it is a cache.
 *
 * `app/main.tsx` sets `gcTime` to five minutes on every query. A decrypted secret placed in a query
 * result would therefore sit in memory for five minutes after the screen that showed it was closed,
 * keyed by something derived from the transfer id, and would be handed straight back to the next
 * component that asked for the same key. So:
 *
 *   * **the value is never a query.** It is the result of a mutation, and that mutation is created
 *     with `gcTime: 0` so nothing survives the observer unmounting.
 *   * **the value never becomes the mutation's data either.** `revealSecret` hands the plaintext to
 *     a callback the caller supplies and returns only the transfer id and the purpose label, so what
 *     TanStack Query retains between the mutation settling and the component unmounting is metadata
 *     the sender chose, not the secret.
 *   * **the plaintext being sent never becomes mutation variables.** `SendSecretInput` carries a
 *     `readValue` function rather than the string. The value lives in the textarea the person typed
 *     it into, is read at the moment of submission, and is cleared from that node afterwards. One
 *     copy, in one place, that the screen can empty.
 *   * **no id and no value is ever put in a URL, `localStorage`, `sessionStorage` or a toast.** The
 *     recipient's link carries the transfer id, which is a name for a row and not a capability:
 *     `public.consume_one_time_secret` re-checks the recipient and consumes a fresh single-use
 *     step-up grant whoever presents it.
 *   * **the service worker caches none of it.** `app/service-worker/sw.ts` excludes `/api/*` by
 *     name, which is stated here rather than relied on quietly.
 *
 * ---
 *
 * **Why the refusal sentences live here rather than in `app/lib/errors.ts`.**
 *
 * `toPortalFailure` reads a `PORTAL_[A-Z_]+` code out of a PostgREST failure. Half of what this
 * feature can be refused with is not that shape: the Worker has its own lowercase vocabulary
 * (`secret_unrecoverable`, `secret_value_too_large`, `secret_purpose_label_invalid`,
 * `secret_crypto_unavailable`), and `requestStepUpGrant` answers in a third vocabulary of its own
 * that `app/auth/stepUp.ts` already carries sentences for. One screen meets all three, and no other
 * screen meets any of the secret-specific ones.
 *
 * So this module does what `app/auth/stepUp.ts` does for the same reason: it owns the sentences for
 * the refusals only it can produce, and falls through to `toPortalFailure` for the codes the whole
 * portal shares. `errors.ts` stays the table of codes more than one surface meets.
 */

/* -------------------------------------------------------------- the read model */

export const secretTransfersKey = ["portal", "secret-transfers"] as const;

export type SecretDirection = "sent" | "received";
export type SecretStatus = "waiting" | "viewed" | "expired" | "cancelled";

export interface SecretTransfer {
  id: string;
  direction: SecretDirection;
  tenant_id: string;
  /** The recipient on a sent row, the sender on a received one. Never both. */
  counterparty_user_id: string | null;
  purpose_label: string;
  status: SecretStatus;
  created_at: string;
  expires_at: string;
  viewed_at: string | null;
}

/**
 * The caller's own transfers, both directions, status and metadata only.
 *
 * `public.my_one_time_secret_transfers` takes no argument at all, which is what keeps it from being
 * the enumeration path `tests/rpc-surface-reachability.test.ts` warns about: there is nothing to
 * enumerate with. The migration argues it in full.
 *
 * `staleTime: 0` overrides the thirty seconds `main.tsx` sets for everything else, and it is not a
 * preference. A sender who cancels a transfer and a recipient who opens one both need the next read
 * to be the truth rather than a copy from half a minute ago, because what is on screen is the only
 * place a person can see that a value they sent has been opened.
 */
export function useSecretTransfers(enabled = true): UseQueryResult<SecretTransfer[]> {
  return useQuery({
    queryKey: secretTransfersKey,
    enabled,
    staleTime: 0,
    queryFn: async () => {
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase.rpc("my_one_time_secret_transfers");

      if (error) {
        throw error;
      }
      return (data ?? []) as SecretTransfer[];
    }
  });
}

/* ------------------------------------------------------------ the recipient list */

export const secretRecipientsKey = (tenantId: string) =>
  ["portal", "secret-recipients", tenantId] as const;

export interface SecretRecipient {
  userId: string;
  /** The contact name for a colleague. Null for Petrovic, whom the screen names itself. */
  name: string | null;
  kind: "petrovic" | "colleague";
}

/**
 * Who a secret may be addressed to: Petrovic, and the caller's colleagues.
 *
 * Until 2026-09-13 this was two direct selects over `public.memberships` and `public.contacts`, which
 * is right for colleagues and cannot produce Petrovic, whose account is on an allow-list no browser
 * role can read. `public.secret_recipients` answers both in one read, Petrovic first, and offers only
 * the admin accounts that hold a verified factor, because a transfer to any other would be refused.
 *
 * **What it deliberately does not say is which colleagues hold a verified factor.** That is the one
 * refusal a sender is meant to act on, and `20260903090001` places the check underneath the published
 * five-per-hour allowance so that the answer costs an attempt. So the list offers every active
 * colleague and the refusal does the telling.
 */
export async function fetchSecretRecipients(tenantId: string): Promise<SecretRecipient[]> {
  const { data, error } = await getSupabaseBrowserClient().rpc("secret_recipients", {
    p_tenant_id: tenantId
  });

  // A failed read throws rather than resolving to a shorter list: an empty picker produced by a
  // dropped request would tell a sender there is nobody to send to.
  if (error) {
    throw error;
  }

  return ((data ?? []) as { user_id: string; full_name: string | null; recipient_kind: string }[]).map(
    (row) => ({
      userId: row.user_id,
      name: row.full_name,
      kind: row.recipient_kind === "petrovic" ? "petrovic" : "colleague"
    })
  );
}

export function useSecretRecipients(
  tenantId: string | null,
  enabled = true
): UseQueryResult<SecretRecipient[]> {
  return useQuery({
    queryKey: secretRecipientsKey(tenantId ?? "none"),
    enabled: Boolean(tenantId) && enabled,
    queryFn: () => fetchSecretRecipients(tenantId as string)
  });
}

/* ------------------------------------------------------------------- refusals */

/**
 * A refusal that already knows what it wants to say.
 *
 * The code is kept beside the sentence because two screens branch on it rather than on the prose:
 * `PORTAL_RECIPIENT_MFA_REQUIRED` gets a longer explanation and a different shape, and
 * `secret_unrecoverable` and `PORTAL_SECRET_NOT_VIEWABLE` must never be offered a retry.
 */
export class SecretFailure extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "SecretFailure";
    this.code = code;
  }
}

/** The two codes a screen must never put a retry button under. Exported so both routes agree. */
export const SECRET_FINAL_CODES = ["PORTAL_SECRET_NOT_VIEWABLE", "secret_unrecoverable"] as const;

export function isFinalSecretFailure(error: unknown): boolean {
  return (
    error instanceof SecretFailure &&
    (SECRET_FINAL_CODES as readonly string[]).includes(error.code)
  );
}

/**
 * The sentences for the refusals only this feature can produce.
 *
 * Each one says what happened, whether anything changed, and what the person does next, which is the
 * rule `STEP_UP_FAILURE_TEXT` states about itself and holds for the same reason: every caller of
 * this list is standing in front of an action that either happened or did not.
 */
export const SECRET_FAILURE_TEXT: Record<string, string> = {
  /*
   * The one refusal plan.md 5.10 asks the interface to carry properly, in its own words: a transfer
   * to an account without MFA "is refused, and the sender is told why. This is the only pressure in
   * the product that will ever move client users onto MFA."
   *
   * So it says the reason rather than the rule. Re-authentication on a password-only account is an
   * extra step and no security, because whoever holds the password holds the secret, and the
   * sentence is what has to make that land with somebody who is about to give up and send the value
   * over mail instead.
   */
  PORTAL_RECIPIENT_MFA_REQUIRED:
    "Mottagaren har inte tvåfaktorsinloggning påslagen, så hemligheten skickades inte. En hemlighet " +
    "som bara skyddas av ett lösenord skyddas inte alls: den som har lösenordet har hemligheten. Be " +
    "mottagaren slå på tvåfaktor under Inställningar, så går den att skicka.",
  PORTAL_RECIPIENT_NOT_IN_TENANT:
    "Mottagaren finns inte i er organisation längre. Ingenting skickades. Ladda om sidan, så visar " +
    "listan vilka som går att välja nu.",
  // Raised by `public.consume_one_time_secret` for a transfer that is not the caller's, and for one
  // that does not exist at all. The gateway answers both the same way on purpose, so the sentence
  // keeps them together rather than guessing which.
  PORTAL_UNKNOWN_SECRET_TRANSFER:
    "Hemligheten finns inte, eller är inte skickad till er. Ingenting har ändrats.",
  // The one-view rule doing its job. There is no retry that can work, so the sentence offers none.
  PORTAL_SECRET_NOT_VIEWABLE:
    "Hemligheten går inte att visa. Den är redan öppnad, återkallad eller utgången. Den går inte " +
    "att visa igen. Be avsändaren skicka en ny.",
  PORTAL_SECRET_NOT_CANCELLABLE:
    "Hemligheten går inte att återkalla längre. Den är redan öppnad, återkallad eller utgången. " +
    "Ladda om sidan, så visar listan vad som gäller nu.",
  /*
   * The fail-closed path, said as it is rather than softened.
   *
   * `worker/src/routes/secrets.ts` and architecture.md section 10 are explicit: "Failure after
   * database consumption never makes the value viewable a second time; the UI reports that support
   * is required. This conservative failure mode preserves 'one view'." The row is already marked
   * viewed and its ciphertext is already gone, so there is no retry, no fallback and no second
   * attempt. A screen that offered one would be promising something the design has ruled out.
   */
  secret_unrecoverable:
    "Hemligheten är förbrukad och kunde inte visas. Den går inte att hämta tillbaka. Skriv i " +
    "chatten så hör vi av oss, och be avsändaren skicka en ny.",
  secret_value_too_large:
    "Hemligheten är för lång. Det här är till för lösenord och nycklar, inte för filer. Behöver ni " +
    "skicka en fil, skriv i chatten.",
  secret_purpose_label_invalid: "Skriv vad hemligheten gäller, med högst 200 tecken.",
  secret_crypto_unavailable:
    "Vi kunde inte kryptera hemligheten just nu, så ingenting skickades och ingenting sparades. " +
    "Försök igen, och skriv i chatten om det upprepas.",
  invalid_request_body:
    "Något i formuläret gick inte att skicka. Ingenting skickades. Ladda om sidan och försök igen."
};

/**
 * One sentence for whatever the code was, in the order the codes are trustworthy.
 *
 * This feature's own vocabulary first, because it is the most specific. Then the portal-wide table
 * through `toPortalFailure`, which is what answers `PORTAL_RATE_LIMITED`, `PORTAL_UNAUTHENTICATED`,
 * `PORTAL_FORBIDDEN_TENANT_WRITE` and the offline case. Anything else becomes the portal's own
 * unexpected-error sentence rather than a raw code on a client's screen.
 */
export function sentenceForSecretCode(code: string): string {
  return SECRET_FAILURE_TEXT[code] ?? toPortalFailure(new Error(code)).message;
}

/** What a screen renders. A `SecretFailure` already carries its sentence; anything else is asked. */
export function secretFailureText(error: unknown): string {
  if (error instanceof SecretFailure) {
    return error.message;
  }
  return toPortalFailure(error).message;
}

/* --------------------------------------------------------------- the step-up */

/**
 * The two actions this feature asks a grant for.
 *
 * `public.request_step_up_grant` accepts four names and `app/auth/stepUp.ts` declares a `StepUpAction`
 * union, whose own comment said it "is widened when a screen that asks for one of the other three
 * exists, and not before". This is that screen, and the union was widened on 2026-09-12 to hold
 * these two. So this type is a narrowing of that one rather than a stand-in for it: it is what this
 * module may ask for, which is not `management_cancellation` and not `membership_change`, and the
 * two-member union is what stops a copy-paste inside this file from asking for either.
 */
export type SecretStepUpAction = Extract<
  StepUpAction,
  "create_one_time_secret" | "consume_one_time_secret"
>;

/**
 * Viewing a secret is TOTP whoever asks, and that is the database's decision rather than this file's.
 *
 * `public.request_step_up_grant` sets the TOTP branch for `consume_one_time_secret` before it looks at
 * whether the caller holds a factor, and refuses an `aal1` session. architecture.md section 4 says the
 * same in one line: "Secret viewing always uses the fresh TOTP path; password-only users are refused."
 *
 * **Sending is not, since 2026-09-13.** The sender's factor was never a requirement in plan.md 5.10,
 * which puts it on the recipient; it arrived because the two secret actions were treated as a pair,
 * and it left a client with no factor mailing Petrovic the password instead. So the send takes the
 * step-up from `app/auth/useStepUpProof.tsx`, which asks for the factor when the account has one and
 * for a fresh password when it does not, and only the view comes through here.
 */
async function grantFor(
  action: SecretStepUpAction,
  factorId: string,
  code: string,
  tenantId: string
): Promise<string> {
  const result = await requestStepUpGrant({
    factorId,
    code,
    tenantId,
    action
  });

  if (!result.ok) {
    const reason: StepUpFailure = result.reason;
    throw new SecretFailure(`STEP_UP_${reason}`, STEP_UP_FAILURE_TEXT[reason]);
  }

  return result.token;
}

/* ------------------------------------------------------------------ the Worker */

/**
 * The Worker is same-origin, and the literal path is what makes the route reachable at all.
 *
 * The same helper `app/data/documentQueries.ts` carries, for the same reason: architecture.md
 * section 6 has the Worker serve both `/api/*` and the application through its ASSETS binding, so a
 * relative path is the whole address. Against the Vite dev server there is nothing behind `/api`,
 * which is why the browser suite intercepts these calls and the flow itself is proven end to end in
 * `tests/worker/secret-flow.test.ts`.
 *
 * A refusal is turned into a `SecretFailure` here rather than at the call sites, so every path
 * through this module produces one kind of error object.
 */
async function callSecretsWorker(path: string, body: unknown): Promise<Response> {
  const supabase = getSupabaseBrowserClient();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  if (!token) {
    throw new SecretFailure("PORTAL_UNAUTHENTICATED", sentenceForSecretCode("PORTAL_UNAUTHENTICATED"));
  }

  const response = await fetch(path, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const failure = (await response.json().catch(() => null)) as { error?: string } | null;
    const code = failure?.error ?? "PORTAL_UNEXPECTED";

    throw new SecretFailure(code, sentenceForSecretCode(code));
  }

  return response;
}

/**
 * The two paths that carry an id, assembled by concatenation and never by interpolation.
 *
 * `worker/src/routes/secrets.ts` answers `POST /api/secrets/{id}/consume` and
 * `POST /api/secrets/{id}/cancel`, and unlike `/api/files/*` it returns no path for either: the
 * create response is `{ id }` and nothing else. So the browser has to build them, and the shape
 * matters. `tests/worker-route-reachability.test.ts` matches string literals and asserts that no
 * template literal in `app/` interpolates into an `/api/` path, because a path assembled that way is
 * a call the measurement cannot see. A concatenation keeps the literal `"/api/secrets/"` in the file
 * and therefore in the index, which is what makes the group count as called, and leaves that guard
 * meaning what it says.
 *
 * **`create` is not one of the two and is written out in full below, which is not a style
 * preference.** The prefix alone is not enough for it: `isCalledByBrowser` in that test asks whether
 * some literal found in `app/` equals the Worker's path or is a prefix of it, and `"/api/secrets/"`
 * is neither for `/api/secrets/create`. Building it as `SECRETS_PREFIX + "create"` therefore left a
 * fully implemented route reading as uncalled on the first run of the suite after this screen was
 * built, which is the measurement working. The two paths above genuinely cannot be literals because
 * an id sits in the middle of them; this one has nothing in it, so it is written as what it is.
 */
const SECRETS_PREFIX = "/api/secrets/";
const SECRETS_CREATE_PATH = "/api/secrets/create";

/* --------------------------------------------------------------------- create */

export interface SendSecretInput {
  /** The client's tenant. For a Petrovic session, the tenant of the client being sent to. */
  tenantId: string;
  recipientUserId: string;
  purposeLabel: string;
  /**
   * Mints the step-up grant, from the proof the screen collected.
   *
   * A function rather than a token, so the grant is minted inside the send and not a moment earlier:
   * it lives 300 seconds, and a token held in component state for as long as a person hesitates is a
   * capability sitting in memory for no reason.
   */
  requestGrant: (tenantId: string) => Promise<StepUpResult>;
  /**
   * Reads the plaintext at the moment of submission.
   *
   * A function rather than the string, so that the value is never part of the mutation's variables
   * and therefore never part of anything TanStack Query retains. The value lives in the textarea the
   * person typed it into and the screen empties that node on success.
   */
  readValue: () => string;
}

/**
 * Mint a grant, then send, in that order and inside one call.
 *
 * `gcTime: 0` on the mutation itself. The variables carry the purpose label, which is the sender's own
 * words, and two functions. None of it is the secret, and none of it has any reason to outlive the
 * screen.
 */
export function useSendSecret() {
  const queryClient = useQueryClient();

  return useMutation({
    gcTime: 0,
    mutationFn: async (input: SendSecretInput): Promise<{ id: string }> => {
      const grant = await input.requestGrant(input.tenantId);

      if (!grant.ok) {
        const reason: StepUpFailure = grant.reason;
        throw new SecretFailure(`STEP_UP_${reason}`, STEP_UP_FAILURE_TEXT[reason]);
      }

      const response = await callSecretsWorker(SECRETS_CREATE_PATH, {
        tenant_id: input.tenantId,
        recipient_user_id: input.recipientUserId,
        purpose_label: input.purposeLabel,
        value: input.readValue(),
        step_up_token: grant.token
      });

      // The Worker answers `{ id }` and nothing else, which is all the sender needs: the recipient is
      // told by `public.create_one_time_secret` writing them a notification, so the id does not have
      // to travel through a person.
      return (await response.json()) as { id: string };
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: secretTransfersKey })
  });
}

/* --------------------------------------------------------------------- cancel */

export interface CancelSecretInput {
  id: string;
}

/**
 * Withdrawing a waiting transfer, which takes no step-up and should not.
 *
 * `20260903140001` decided that deliberately: "demanding a fresh TOTP challenge before somebody may
 * stop exposing a value is friction pointing the wrong way. A sender who realises they sent the
 * wrong thing must be able to withdraw it now, not after a second factor round trip."
 */
export function useCancelSecret() {
  const queryClient = useQueryClient();

  return useMutation({
    gcTime: 0,
    mutationFn: async (input: CancelSecretInput): Promise<{ id: string }> => {
      await callSecretsWorker(SECRETS_PREFIX + input.id + "/cancel", {});
      return { id: input.id };
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: secretTransfersKey })
  });
}

/* -------------------------------------------------------------------- consume */

export interface RevealSecretInput {
  id: string;
  tenantId: string;
  factorId: string;
  code: string;
}

interface ConsumeResponse {
  id: string;
  purpose_label: string;
  value: string;
}

/**
 * One view, and nothing in this function is allowed to make a second one possible.
 *
 * `onReveal` is where the plaintext goes and it is the only place it goes. The mutation returns the
 * purpose label and the id, so `mutation.data` carries metadata and never the value, and `gcTime: 0`
 * means even that is dropped the moment the screen unmounts.
 *
 * The callback is held in a ref by the caller rather than passed through the mutation's variables,
 * because variables are retained by the mutation cache in exactly the way this whole module is
 * written to avoid.
 *
 * **What this function deliberately does not do is retry.** `worker/src/routes/secrets.ts` records
 * a refusal through `public.record_secret_view_refusal` and answers `secret_unrecoverable` when a
 * decryption fails after the conditional statement has already run, and that path is final by
 * design: the row is `viewed` and its ciphertext is null. So there is no retry option here, no
 * second attempt and no fallback, and `isFinalSecretFailure` is what the screen uses to make sure it
 * does not offer one either.
 */
export function useRevealSecret(onReveal: (value: string) => void) {
  return useMutation({
    gcTime: 0,
    retry: false,
    mutationFn: async (
      input: RevealSecretInput
    ): Promise<{ id: string; purposeLabel: string }> => {
      const stepUpToken = await grantFor(
        "consume_one_time_secret",
        input.factorId,
        input.code,
        input.tenantId
      );

      const response = await callSecretsWorker(SECRETS_PREFIX + input.id + "/consume", {
        step_up_token: stepUpToken
      });

      const consumed = (await response.json()) as ConsumeResponse;

      onReveal(consumed.value);

      return { id: consumed.id, purposeLabel: consumed.purpose_label };
    }
  });
}
